<template>
  <div id="variableRateMode">
    <PluginHeader
      title="Variable Rate Mode"
      :subtitle="currentFarm ? 'Configure variable rate applications for ' + currentFarm.name : 'Configure variable rate applications'"
      icon="fas fa-sliders-h"
      :backQuery="{ mypad: '1' }"
    />
    <div class="container-fluid">
      <div class="row">
        <div class="col-12">
          <div class="card">
            <div class="card-body">
              <div class="alert alert-warning mt-0 mb-2" style="font-size: 0.9rem;">
                <i class="fas fa-exclamation-triangle"></i>
                <strong>Note:</strong> Variable rate maps currently work best with single field datasets. 
                Multi-field datasets may experience zone generation issues.
              </div>
              <div class="row">
                <div class="col-md-6">
                  <h5>Dataset Selection</h5>
                  
                  <div class="dataset-browser">
                    <!-- Breadcrumbs -->
                    <div class="mb-2">
                    <span
                      v-for="(crumb, i) in breadcrumbs"
                      v-bind:key="i"
                    >
                      <b-badge
                        pill
                        variant="primary"
                        @click="launchFolder(crumb, true)"
                        class="clickable"
                      >{{ crumb.name }}</b-badge><span v-if="(i + 1) !== breadcrumbs.length"> / </span>
                    </span>
                  </div>

                  <!-- Folders -->
                  <div class="list-group mb-2 folders">
                    <div
                      v-for="(folder, i) in availableFolders"
                      v-bind:key="i"
                      class="list-container"
                    >
                      <a
                        @click="launchFolder(folder)"
                        class="list-group-item list-group-item-action clickable mb-2 folder"
                      >
                        <p class="lead mb-1"><i class="fas fa-folder"></i> {{ folder.name }}</p>
                        <p class="mb-1">{{ folder.description }}</p>
                      </a>
                    </div>
                  </div>

                  <!-- Datasets -->
                  <div class="list-group sets">
                    <div
                      v-for="(dataset, i) in availableDatasets"
                      v-bind:key="i"
                      class="list-container"
                    >
                      <a
                        @click="selectDataset(dataset)"
                        class="list-group-item list-group-item-action clickable mb-2"
                        :class="{ 'selected-dataset': selectedDataset === dataset.id }"
                      >
                        <p class="lead mb-1">
                          <i v-if="dataset.dynamic" class="fas fa-satellite-dish" v-b-tooltip.hover.top="'Dynamic Dataset'"></i>
                          <i v-if="dataset.file && dataset.file.raster" class="fas fa-border-none" v-b-tooltip.hover.top="'Raster Dataset'"></i>
                          {{ dataset.name }}
                          <span v-if="isVRCompatible(dataset)" class="badge badge-success ml-2" v-b-tooltip.hover.top="'Variable Rate Compatible'">
                            <i class="fas fa-sliders-h"></i> VR Compatible
                          </span>
                        </p>
                        <p class="mb-1">{{ dataset.description }}</p>
                        <small>Updated: {{ dataset.updatedOn | formatDate }}</small>
                      </a>
                    </div>
                  </div>

                  <div v-if="availableDatasets.length === 0 && availableFolders.length === 0" class="text-center text-muted">
                    <i class="fas fa-folder-open fa-2x mb-2"></i>
                    <p>No datasets or folders available in this location.</p>
                  </div>
                  </div>
                </div>
                
                <div class="col-md-6">
                  <h5>Preview</h5>
                  <div v-if="selectedDataset" class="preview-container">
                    <div class="alert alert-info">
                      <strong>Selected Dataset:</strong> {{ getDatasetName(selectedDataset) }}
                    </div>
                    
                    <!-- VR Compatibility Check -->
                    <div v-if="getSelectedDataset() && isVRCompatible(getSelectedDataset())" class="rate-summary">
                      <div class="alert alert-success">
                        <i class="fas fa-check-circle"></i> <strong>Variable Rate Compatible</strong>
                        <p class="mb-0 mt-2">This dataset is ready for variable rate applications.</p>
                      </div>
                    </div>
                    
                    <div v-else-if="getSelectedDataset()" class="rate-summary">
                      <div class="alert alert-warning">
                        <i class="fas fa-exclamation-triangle"></i> <strong>Not Variable Rate Compatible</strong>
                      </div>
                      <div class="vr-instructions">
                        <h6>To make this dataset VR compatible:</h6>
                        <ul class="list-unstyled">
                          <li><i class="fas fa-check text-success"></i> <strong>Visualize by value:</strong> Set color by to "Value Based"</li>
                          <li><i class="fas fa-check text-success"></i> <strong>AND choose one of:</strong></li>
                          <li class="ml-3">• Polygon dataset type</li>
                          <li class="ml-3">• Equal interval zones</li>
                          <li class="ml-3">• Jenks natural breaks zones</li>
                          <li class="ml-3">• Categorical visualization</li>
                        </ul>
                      </div>
                    </div>
                    
                    <!-- Configure Dataset Settings Button - Always Available -->
                    <div class="mt-3">
                      <button class="btn btn-outline-primary btn-sm" @click="goToDatasetSettings()">
                        <i class="fas fa-cog"></i> Configure Dataset Settings
                      </button>
                    </div>
                    
                    <!-- Zone Rate Configuration -->
                    <div v-if="selectedDatasetData && selectedDatasetData.vizSettings && zoneRates.length > 0" class="mt-3">
                      <h6>Zone Rate Configuration</h6>
                      <div class="zone-rates-table">
                                            <div class="zone-rate-row header">
                      <div class="zone-color"></div>
                      <div class="zone-info">Zone</div>
                      <div class="zone-rate">Rate</div>
                    </div>
                        <div 
                          v-for="(zone, index) in zoneRates" 
                          :key="index" 
                          class="zone-rate-row"
                        >
                          <div class="zone-color">
                                                    <div 
                          class="color-dot" 
                          :style="{ backgroundColor: zone.color }"
                        ></div>
                          </div>
                          <div class="zone-info">
                            {{ formatZoneValue(zone) }}
                          </div>
                          <div class="zone-rate">
                            <input 
                              type="number" 
                              v-model="zone.rate" 
                              @input="updateLoadedRate(zone.key, zone.rate)"
                              class="form-control rate-input"
                              :placeholder="'Rate for Zone ' + (index + 1)"
                              min="0"
                              step="0.1"
                            >
                          </div>
                        </div>
                      </div>
                    </div>
                    
                    <!-- Dataset Preview Map -->
                    <div class="mt-3">
                      <h6>Dataset Preview</h6>
                      <div class="map-preview">
                        <div id="variableRatePreviewMap" style="height: 300px; width: 100%; border-radius: 4px; border: 1px solid #ddd; position: relative;">
                          <!-- Map Loading Spinner -->
                          <div v-if="mapLoading" class="map-spinner">
                            <div class="spinner-border text-primary" role="status">
                              <span class="sr-only">Loading map...</span>
                            </div>
                            <p class="mt-2 mb-0 text-muted">Loading dataset preview...</p>
                          </div>
                        </div>
                      </div>
                    </div>
                    <!-- Start Variable Rate Button -->
                    <div v-if="getSelectedDataset() && isVRCompatible(getSelectedDataset()) && zoneRates.length > 0" class="mt-4">
                      <button class="btn btn-success btn-lg btn-block" @click="startVariableRate">
                        <i class="fas fa-play"></i> Start Variable Rate
                      </button>
                    </div>
                  </div>
                  <div v-else class="text-center text-muted">
                    <i class="fas fa-chart-area fa-3x mb-3"></i>
                    <p>Select a dataset to configure variable rate settings</p>
                  </div>
                </div>
              </div>

            </div>
          </div>
        </div>
      </div>
    </div>
    
    <!-- Full Screen Variable Rate Map -->
    <div v-if="isVariableRateActive" class="fullscreen-map">
          <div class="fullscreen-map-header">
      <button class="btn btn-danger" @click="stopVariableRate">
        <i class="fas fa-stop"></i> Stop Variable Rate
      </button>
      <button class="btn btn-warning" @click="toggleSimulationMode">
        <i class="fas fa-play" v-if="!isSimulationMode"></i>
        <i class="fas fa-pause" v-if="isSimulationMode"></i>
        {{ isSimulationMode ? 'Stop' : 'Start' }} Simulation
      </button>
      <div class="current-rate-display">
        <span class="rate-label">Current Rate:</span>
        <span class="rate-value">{{ currentRate !== null ? currentRate : 0 }}</span>
      </div>
      <div class="gps-status" v-if="userLocation">
        <span class="status-label">GPS:</span>
        <span class="status-value">{{ isSimulationMode ? 'Simulated' : 'Active' }} ({{ userLocation.accuracy.toFixed(1) }}m)</span>
      </div>
      <div class="gps-status" v-else>
        <span class="status-label">GPS:</span>
        <span class="status-value">Searching...</span>
      </div>
    </div>
      <div id="fullScreenVariableRateMap" class="fullscreen-map-container"></div>
    </div>
  </div>
</template>

<script>
import moment from 'moment'
import firebase from 'firebase/compat/app'
import jenks from 'turf-jenks'
import PluginHeader from './_shared/PluginHeader.vue'
const turf = require('@turf/turf')
const fb = require('../../firebaseConfig.js')

export default {
  name: 'VariableRateMode',
  components: { PluginHeader },
  data () {
    return {
      selectedDataset: '',
      previewMap: null,
      selectedDatasetData: null,
      selectedDatasetGeoJson: null,
      mapLoading: false,
      generatedZones: null,
      // Variable Rate Mode
      isVariableRateActive: false,
      fullScreenMap: null,
      userLocation: null,
      locationWatcher: null,
      currentZone: null,
      currentRate: null,
      qtWebChannel: null,
      // WebSocket for rate communication
      websocket: null,
      websocketConnected: false,
      userBearing: 0,
      userMarker: null,
      // GPS Simulation
      isSimulationMode: false,
      simulationInterval: null,
      simulationPath: [],
      currentSimulationIndex: 0,
      // Zone Rate Persistence
      loadedZoneRates: {},
      zoneRatesLoaded: false,
      // Zone Check Debouncing
      lastZoneCheckTime: 0,
      zoneCheckDebounceMs: 500, // Debounce zone checks by 500ms
      isCheckingZone: false, // Prevent multiple simultaneous zone checks
      // Map monitoring
      mapObserver: null
    }
  },
  computed: {
    availableDatasets () {
      // Get datasets from the Vuex store
      return this.$store.state.datasets || []
    },
    availableFolders () {
      // Get folders from the Vuex store
      return this.$store.state.folders || []
    },
    currentFarm () {
      return this.$store.state.farm
    },
    breadcrumbs: {
      get () {
        return this.$store.state.breadcrumbs || [{ name: 'Root', id: 'root' }]
      },
      set (value) {
        this.$store.commit('setStateProperty', { property: 'breadcrumbs', value: value })
      }
    },
    zoneRates () {
      if (!this.selectedDatasetData || !this.selectedDatasetData.vizSettings) {
        return []
      }
      
      const vizSettings = this.selectedDatasetData.vizSettings
      
      // For point datasets that need zone generation, wait until zones are ready
      if (vizSettings.type === 'circle' && 
          vizSettings.colorBy === 'valueBased' && 
          this.selectedDatasetGeoJson && 
          this.selectedDatasetGeoJson.features.length > 0 &&
          this.selectedDatasetGeoJson.features[0].geometry.type === 'Point' &&
          !this.generatedZones) {
        console.log('Waiting for zone generation to complete...')
        return []
      }
      
      // Get the data that's actually being rendered (either generated zones or original data)
      const renderedData = this.getRenderedData()
      if (!renderedData || !renderedData.features || renderedData.features.length === 0) {
        return []
      }
      
      // Extract unique zones from the rendered data
      const uniqueZones = new Map()
      
      renderedData.features.forEach(feature => {
        // All features should now have zoneNumber property from our processing
        if (feature.properties.zoneNumber) {
          const zoneNumber = feature.properties.zoneNumber
          const zoneKey = feature.properties.zone || zoneNumber
          const actualValue = feature.properties[vizSettings.colorHeader] || zoneNumber
          
          if (!uniqueZones.has(zoneNumber)) {
            uniqueZones.set(zoneNumber, {
              value: actualValue,
              count: 1,
              zoneIndex: parseInt(zoneNumber) - 1, // Convert to 0-based index for color lookup
              zoneNumber: zoneNumber,
              zoneKey: zoneKey
            })
          } else {
            uniqueZones.get(zoneNumber).count++
          }
        }
      })
      
      // Convert to array and sort by zoneNumber
      const zones = Array.from(uniqueZones.values())
        .map(zone => {
          // Check if we have a loaded rate for this zone
          const loadedRate = this.loadedZoneRates[zone.zoneNumber] || this.loadedZoneRates[zone.value] || 50
          
          return {
            key: zone.zoneNumber,
            value: zone.value,
            count: zone.count,
            zoneIndex: zone.zoneIndex,
            zoneNumber: zone.zoneNumber,
            zoneKey: zone.zoneKey,
            color: this.getZoneColorFromIndex(zone.zoneIndex, vizSettings),
            rate: loadedRate
          }
        })
        .sort((a, b) => {
          // Sort by zoneNumber to ensure zones appear in order (1, 2, 3, 4, 5...)
          return parseInt(a.zoneNumber) - parseInt(b.zoneNumber)
        })
      
      console.log('Zone rates computed from rendered data:', zones.length, 'zones')
      console.log('Sorted zones:', zones)
      console.log('Rendered data features for zone computation:', renderedData.features.map(f => ({
        zoneNumber: f.properties.zoneNumber,
        zone: f.properties.zone,
        colorHeader: f.properties[vizSettings.colorHeader]
      })))
      return zones
    }
  },
  watch: {
    loadedZoneRates: {
      handler(newVal) {
        console.log('loadedZoneRates changed:', newVal)
        // Force reactivity update
        this.$nextTick(() => {
          this.$forceUpdate()
        })
      },
      deep: true
    },
    generatedZones: {
      handler(newVal) {
        console.log('Generated zones updated:', newVal ? newVal.features.length : 0, 'features')
        console.log('Map status before generatedZones update:')
        this.checkMapStatus()
        
        // Force map to resize and render properly after zones are generated
        this.$nextTick(() => {
          console.log('Map status after generatedZones $nextTick:')
          this.checkMapStatus()
          
          // Force map resize to ensure proper rendering after zone generation
          if (this.previewMap && this.previewMap.loaded()) {
            console.log('Forcing map resize after zone generation...')
            this.previewMap.resize()
            setTimeout(() => {
              this.checkMapStatus()
            }, 200)
          }
        })
      }
    }
  },
  mounted() {
    // Handle window resize to prevent map disappearing
    window.addEventListener('resize', this.handleResize)
  },
  beforeDestroy() {
    // Clean up the map when component is destroyed
    if (this.previewMap) {
      this.previewMap.remove()
    }
    // Stop map monitoring
    this.stopMapMonitoring()
    // Remove resize listener
    window.removeEventListener('resize', this.handleResize)
    // Close WebSocket connection
    if (this.websocket) {
      this.websocket.close()
      this.websocket = null
      this.websocketConnected = false
    }
  },
  created () {
    // No need to load datasets manually - they come from Vuex store
  },
  methods: {
    handleResize() {
      // Trigger map resize to prevent disappearing
      if (this.previewMap) {
        console.log('Handling window resize, triggering map resize...')
        this.previewMap.resize()
      }
    },
    checkMapStatus() {
      console.log('Map status check:')
      console.log('- previewMap exists:', !!this.previewMap)
      console.log('- mapLoading:', this.mapLoading)
      
      const container = document.getElementById('variableRatePreviewMap')
      console.log('- container exists:', !!container)
      if (container) {
        console.log('- container visible:', container.offsetWidth > 0 && container.offsetHeight > 0)
        console.log('- container dimensions:', container.offsetWidth, 'x', container.offsetHeight)
        console.log('- container display:', window.getComputedStyle(container).display)
        console.log('- container visibility:', window.getComputedStyle(container).visibility)
        console.log('- container opacity:', window.getComputedStyle(container).opacity)
      }
      
      if (this.previewMap) {
        console.log('- map loaded:', this.previewMap.loaded())
        console.log('- map style loaded:', this.previewMap.isStyleLoaded())
        console.log('- map container:', this.previewMap.getContainer())
        console.log('- map container visible:', this.previewMap.getContainer().offsetWidth > 0 && this.previewMap.getContainer().offsetHeight > 0)
      }
    },
    startMapMonitoring() {
      // Stop any existing observer
      this.stopMapMonitoring()
      
      const container = document.getElementById('variableRatePreviewMap')
      if (!container) {
        console.log('No map container found for monitoring')
        return
      }
      
      console.log('Starting map container monitoring...')
      
      // Create a mutation observer to watch for changes to the map container
      this.mapObserver = new MutationObserver((mutations) => {
        mutations.forEach((mutation) => {
          if (mutation.type === 'childList') {
            console.log('Map container children changed:', mutation)
            this.checkMapStatus()
          } else if (mutation.type === 'attributes') {
            console.log('Map container attribute changed:', mutation.attributeName, mutation.target)
            this.checkMapStatus()
          }
        })
      })
      
      // Start observing
      this.mapObserver.observe(container, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['style', 'class', 'display', 'visibility', 'opacity']
      })
    },
    stopMapMonitoring() {
      if (this.mapObserver) {
        console.log('Stopping map container monitoring...')
        this.mapObserver.disconnect()
        this.mapObserver = null
      }
    },
    getDatasetName (datasetId) {
      const dataset = this.availableDatasets.find(d => d.id === datasetId)
      return dataset ? dataset.name : 'Unknown Dataset'
    },

    startVariableRate () {
      console.log('Starting variable rate mode...')
      console.log('Current zone rates:', this.zoneRates)
      console.log('Loaded zone rates:', this.loadedZoneRates)
      
      // Save zone rates to Firebase before starting
      this.saveZoneRatesToFirebase().then(() => {
        this.isVariableRateActive = true
        this.$nextTick(() => {
          this.initializeFullScreenMap()
          this.startLocationTracking()
          this.initializeWebSocket()
          
          // Check initial zone intersection after a short delay
          setTimeout(() => {
            console.log('Checking initial zone intersection...')
            this.checkZoneIntersection()
          }, 1000)
          
          // Show initial message about GPS
          this.$store.commit('setAlert', {
            active: true,
            message: 'Starting GPS tracking... Please ensure location permissions are enabled.',
            level: 'alert-info',
            timeout: 3000
          })
        })
      }).catch(error => {
        console.error('Error saving zone rates:', error)
        this.$store.commit('setAlert', {
          active: true,
          message: 'Error saving zone rates. Please try again.',
          level: 'alert-warning',
          timeout: 5000
        })
      })
    },
    stopVariableRate () {
      this.isVariableRateActive = false
      this.stopLocationTracking()
      this.stopSimulation() // Stop simulation if running
      if (this.fullScreenMap) {
        this.fullScreenMap.remove()
        this.fullScreenMap = null
      }
      this.currentZone = null
      this.currentRate = null
      // Send 0 when stopping variable rate
      this.sendRateToQT(0)
    },
    initializeFullScreenMap () {
      // Initialize the full-screen map
      // eslint-disable-next-line no-undef
      this.fullScreenMap = new mapboxgl.Map({
        container: 'fullScreenVariableRateMap',
        style: 'mapbox://styles/mapbox/satellite-v9',
        center: [-79.5, 42.4], // Default center
        zoom: 18,
        bearing: 0,
        pitch: 0
      })
      
      this.fullScreenMap.on('load', () => {
        // Add the prescription data
        const renderedData = this.getRenderedData()
        if (renderedData) {
          this.fullScreenMap.addSource('prescription-source', {
            type: 'geojson',
            data: renderedData
          })
          
          // Add the prescription layer
          this.fullScreenMap.addLayer({
            id: 'prescription-layer',
            type: 'fill',
            source: 'prescription-source',
            paint: {
              'fill-color': this.getMapboxColor(this.selectedDatasetData.vizSettings, renderedData),
              'fill-opacity': 0.7,
              'fill-outline-color': '#000000'
            }
          })
          
          // Add zone labels with incremental numbers
          this.fullScreenMap.addLayer({
            id: 'prescription-labels',
            type: 'symbol',
            source: 'prescription-source',
            layout: {
              'text-field': ['get', 'zoneNumber'],
              'text-size': 16,
              'text-anchor': 'center',
              'text-allow-overlap': true,
              'text-ignore-placement': true
            },
            paint: {
              'text-color': '#000000',
              'text-halo-color': '#ffffff',
              'text-halo-width': 2
            }
          })
          
          // Add click handler for zone information
          this.fullScreenMap.on('click', 'prescription-layer', (e) => {
            if (e.features.length > 0) {
              const feature = e.features[0]
              const zoneNumber = feature.properties.zoneNumber || 'Unknown'
              const vizSettings = this.selectedDatasetData.vizSettings
              
              // Determine what information to show based on dataset type
              let infoContent = ''
              
              if (vizSettings.zoneClassification === 'categorical') {
                // For categorical data, show the category name
                const actualValue = feature.properties[vizSettings.colorHeader] || 'N/A'
                infoContent = `<strong>Category:</strong> ${actualValue}`
              } else {
                // For numerical data (equal-interval, jenks), show zone range or just zone info
                if (vizSettings.zoneClassification === 'equal-interval' && vizSettings.minValue !== false && vizSettings.maxValue !== false) {
                  const zoneIndex = parseInt(zoneNumber) - 1
                  const totalZones = vizSettings.zoneStops
                  const range = vizSettings.maxValue - vizSettings.minValue
                  const zoneSize = range / totalZones
                  const zoneMin = vizSettings.minValue + (zoneIndex * zoneSize)
                  const zoneMax = zoneIndex === totalZones - 1 ? vizSettings.maxValue : vizSettings.minValue + ((zoneIndex + 1) * zoneSize)
                  infoContent = `<strong>Value Range:</strong> ${zoneMin.toFixed(2)} - ${zoneMax.toFixed(2)}`
                } else {
                  // For other types, just show zone info
                  infoContent = `<strong>Zone Type:</strong> ${vizSettings.zoneClassification}`
                }
              }
              
              // Show popup with zone information
              // eslint-disable-next-line no-undef
              new mapboxgl.Popup()
                .setLngLat(e.lngLat)
                .setHTML(`
                  <div style="padding: 10px;">
                    <h6 style="margin: 0 0 8px 0; color: #333;">Zone ${zoneNumber}</h6>
                    <p style="margin: 0; font-size: 14px; color: #666;">
                      ${infoContent}
                    </p>
                  </div>
                `)
                .addTo(this.fullScreenMap)
            }
          })
          
          // Change cursor to pointer when hovering over zones
          this.fullScreenMap.on('mouseenter', 'prescription-layer', () => {
            this.fullScreenMap.getCanvas().style.cursor = 'pointer'
          })
          
          this.fullScreenMap.on('mouseleave', 'prescription-layer', () => {
            this.fullScreenMap.getCanvas().style.cursor = ''
          })
        }
        
        // Add user location source and marker
        this.fullScreenMap.addSource('user-location', {
          type: 'geojson',
          data: {
            type: 'FeatureCollection',
            features: []
          }
        })
        
        // Add user location marker (directional arrow)
        this.fullScreenMap.addLayer({
          id: 'user-location-marker',
          type: 'symbol',
          source: 'user-location',
          layout: {
            'text-field': '▲',
            'text-size': 24,
            'text-allow-overlap': true,
            'text-ignore-placement': true,
            'text-rotation-alignment': 'map',
            'text-rotate': ['get', 'bearing']
          },
          paint: {
            'text-color': '#ff0000',
            'text-halo-color': '#ffffff',
            'text-halo-width': 2
          }
        })
        
        // Add user location accuracy circle
        this.fullScreenMap.addLayer({
          id: 'user-location-accuracy',
          type: 'circle',
          source: 'user-location',
          paint: {
            'circle-radius': ['get', 'accuracy'],
            'circle-color': '#007cbf',
            'circle-opacity': 0.2,
            'circle-stroke-color': '#007cbf',
            'circle-stroke-width': 1
          }
        })
      })
    },
    startLocationTracking () {
      if ('geolocation' in navigator) {
        // Stop any existing simulation if running
        this.stopSimulation()
        
        // Ensure we're not already tracking
        if (this.locationWatcher) {
          console.log('Location tracking already active, stopping previous watcher')
          this.stopLocationTracking()
        }
        
        let previousPosition = null
        
        // First, try to get a quick position fix
        navigator.geolocation.getCurrentPosition(
          (position) => {
            console.log('Initial position obtained:', position.coords)
            this.userLocation = {
              lat: position.coords.latitude,
              lng: position.coords.longitude,
              accuracy: position.coords.accuracy || 10
            }
            
            // Update user location on map immediately
            this.updateUserLocationOnMap()
            
            // Center map on user location
            if (this.fullScreenMap) {
              this.fullScreenMap.easeTo({
                center: [this.userLocation.lng, this.userLocation.lat],
                duration: 1000
              })
            }
            
            // Check for zone intersection
            this.checkZoneIntersection()
          },
          (error) => {
            console.error('Initial location error:', error)
            // Continue with watchPosition even if initial position fails
          },
          {
            enableHighAccuracy: false, // Start with low accuracy for faster fix
            timeout: 5000,
            maximumAge: 30000
          }
        )
        
        // Then start continuous tracking
        this.locationWatcher = navigator.geolocation.watchPosition(
          (position) => {
            console.log('Location update:', position.coords)
            const newLocation = {
              lat: position.coords.latitude,
              lng: position.coords.longitude,
              accuracy: position.coords.accuracy || 10
            }
            
            // Calculate bearing if we have a previous position
            if (previousPosition) {
              this.userBearing = this.calculateBearing(
                previousPosition.lat, previousPosition.lng,
                newLocation.lat, newLocation.lng
              )
            }
            
            this.userLocation = newLocation
            previousPosition = newLocation
            
            // Update user location on map
            this.updateUserLocationOnMap()
            
            // Update map to follow user (GPS-style navigation)
            if (this.fullScreenMap) {
              this.fullScreenMap.easeTo({
                center: [this.userLocation.lng, this.userLocation.lat],
                bearing: this.userBearing,
                duration: 1000
              })
            }
            
            // Check for zone intersection
            this.checkZoneIntersection()
          },
          (error) => {
            console.error('Location tracking error:', error)
            let errorMessage = 'Unable to track location.'
            
            switch (error.code) {
              case 1:
                errorMessage = 'Location access denied. Please enable location permissions.'
                break
              case 2:
                errorMessage = 'Location unavailable. Please check your GPS settings.'
                break
              case 3:
                errorMessage = 'Location timeout. Please ensure GPS is enabled and you have a clear view of the sky.'
                break
            }
            
            this.$store.commit('setAlert', {
              active: true,
              message: errorMessage,
              level: 'alert-warning',
              timeout: 8000
            })
          },
          {
            enableHighAccuracy: true, // Use high accuracy for continuous tracking
            timeout: 15000, // Increased timeout
            maximumAge: 5000 // Allow slightly older positions
          }
        )
      } else {
        this.$store.commit('setAlert', {
          active: true,
          message: 'Geolocation is not supported in this browser.',
          level: 'alert-warning',
          timeout: 5000
        })
      }
    },
    stopLocationTracking () {
      if (this.locationWatcher) {
        navigator.geolocation.clearWatch(this.locationWatcher)
        this.locationWatcher = null
      }
    },
    checkZoneIntersection () {
      // Debounce zone checks to prevent multiple rapid calls
      const now = Date.now()
      const timeSinceLastCheck = now - this.lastZoneCheckTime
      console.log('Zone check debounce check - time since last:', timeSinceLastCheck, 'ms, debounce threshold:', this.zoneCheckDebounceMs, 'ms')
      
      if (timeSinceLastCheck < this.zoneCheckDebounceMs) {
        console.log('Zone check debounced - skipping call')
        return
      }
      
      // Prevent multiple simultaneous executions
      if (this.isCheckingZone) {
        console.log('Zone check already in progress - skipping call')
        return
      }
      
      this.isCheckingZone = true
      this.lastZoneCheckTime = now
      console.log('Zone check proceeding - lastZoneCheckTime updated to:', now)
      
      const callId = Math.random().toString(36).substr(2, 9)
      console.log('checkZoneIntersection called [ID:', callId, ']')
      console.log('User location:', this.userLocation)
      console.log('Zone rates length:', this.zoneRates.length)
      console.log('Zone rates details:', this.zoneRates.map(z => ({ key: z.key, value: z.value, rate: z.rate })))
      
      if (!this.userLocation || !this.zoneRates.length) {
        console.log('No user location or zone rates, sending 0')
        // Send 0 if no zones are configured
        this.sendRateToQT(0)
        return
      }
      
      const renderedData = this.getRenderedData()
      console.log('Rendered data features:', renderedData ? renderedData.features.length : 'No data')
      
      if (!renderedData) {
        console.log('No rendered data, sending 0')
        // Send 0 if no data available
        this.sendRateToQT(0)
        return
      }
      
      const userPoint = turf.point([this.userLocation.lng, this.userLocation.lat])
      console.log('User point:', userPoint.geometry.coordinates)
      
      // Check each zone for intersection
      let foundIntersection = false
      let bestZone = null
      let bestRate = 0
      
      console.log('Starting zone intersection check with', this.zoneRates.length, 'zones')
      
      for (const zone of this.zoneRates) {
        console.log('Checking zone:', zone.key, 'with rate:', zone.rate)
        
        // Match by zoneNumber (which is now the key) or by the original zone value
        const zoneFeatures = renderedData.features.filter(f => 
          f.properties.zoneNumber === zone.key || 
          f.properties.zone === zone.value || 
          f.properties.zone === zone.key
        )
        
        console.log('Zone features found:', zoneFeatures.length)
        console.log('Zone feature properties:', zoneFeatures.map(f => ({
          zoneNumber: f.properties.zoneNumber,
          zone: f.properties.zone,
          colorHeader: f.properties[this.selectedDatasetData.vizSettings.colorHeader]
        })))
        
        for (const feature of zoneFeatures) {
          if (turf.booleanPointInPolygon(userPoint, feature)) {
            foundIntersection = true
            bestZone = zone.value
            bestRate = zone.rate
            console.log('Zone intersection found:', {
              zoneNumber: zone.key,
              zoneValue: zone.value,
              rate: zone.rate,
              userLocation: this.userLocation
            })
            console.log('Updated foundIntersection to:', foundIntersection, 'bestZone:', bestZone, 'bestRate:', bestRate)
            // Found an intersection, but continue checking to see if there are multiple matches
            // (though this shouldn't happen with proper zone generation)
          }
        }
      }
      
      console.log('Zone intersection check completed. foundIntersection:', foundIntersection, 'bestZone:', bestZone, 'bestRate:', bestRate)
      
      if (foundIntersection) {
        console.log('Final intersection result [ID:', callId, '] - Zone:', bestZone, 'Rate:', bestRate)
        this.currentZone = bestZone
        this.currentRate = bestRate
        this.sendRateToQT(bestRate)
      } else {
        // No intersection found - send 0
        console.log('No zone intersection found [ID:', callId, '], sending 0')
        this.currentZone = null
        this.currentRate = null
        this.sendRateToQT(0)
      }
      
      // Reset the checking flag
      this.isCheckingZone = false
    },
    initializeWebSocket () {
      console.log('Initializing WebSocket connection...')
      
      try {
        // Create WebSocket connection to your collaborator's server
        this.websocket = new WebSocket('ws://localhost:5678')
        
        this.websocket.onopen = () => {
          console.log('WebSocket connection established')
          this.websocketConnected = true
        }
        
        this.websocket.onmessage = (event) => {
          console.log('Received message from server:', event.data)
          // Handle any messages from the server if needed
        }
        
        this.websocket.onclose = () => {
          console.log('WebSocket connection closed')
          this.websocketConnected = false
          // Try to reconnect after a delay
          setTimeout(() => {
            if (!this.websocketConnected) {
              console.log('Attempting to reconnect WebSocket...')
              this.initializeWebSocket()
            }
          }, 5000)
        }
        
        this.websocket.onerror = (error) => {
          console.log('WebSocket error:', error)
          this.websocketConnected = false
        }
        
      } catch (error) {
        console.log('Failed to initialize WebSocket:', error)
        this.websocketConnected = false
      }
    },
    saveZoneRatesToFirebase () {
      if (!this.selectedDataset || !this.zoneRates.length) {
        return Promise.resolve()
      }
      
      const zoneRatesData = this.zoneRates.map(zone => ({
        key: zone.key,
        value: zone.value,
        rate: zone.rate,
        color: zone.color
      }))
      
      console.log('Saving zone rates to Firebase:', zoneRatesData)
      
      return fb.db.collection('datasets').doc(this.selectedDataset).update({
        variableRateSettings: {
          zoneRates: zoneRatesData,
          lastUpdated: new Date(),
          vizSettings: this.selectedDatasetData.vizSettings
        }
      })
    },
    updateLoadedRate (zoneKey, rate) {
      // Update the loaded rates when user changes them manually
      this.$set(this.loadedZoneRates, zoneKey, rate)
      console.log('Updated loaded rate for zone', zoneKey, 'to', rate)
    },
    loadZoneRatesFromFirebase () {
      if (!this.selectedDataset) {
        console.log('No selected dataset, skipping zone rate loading')
        return Promise.resolve()
      }
      
      console.log('Loading zone rates for dataset:', this.selectedDataset)
      
      return fb.db.collection('datasets').doc(this.selectedDataset).get()
        .then(doc => {
          console.log('Firebase response:', doc.exists ? 'Document exists' : 'Document not found')
          if (doc.exists && doc.data().variableRateSettings) {
            const savedSettings = doc.data().variableRateSettings
            console.log('Loaded saved zone rates:', savedSettings)
            
            // Check if the vizSettings match (to ensure rates are still relevant)
            // Use a more flexible comparison that checks key properties
            if (savedSettings.vizSettings && this.selectedDatasetData.vizSettings) {
              const saved = savedSettings.vizSettings
              const current = this.selectedDatasetData.vizSettings
              
              // Check key properties that affect zone structure
              const vizSettingsMatch = 
                saved.colorBy === current.colorBy &&
                saved.colorHeader === current.colorHeader &&
                saved.zoneClassification === current.zoneClassification &&
                saved.zoneStops === current.zoneStops &&
                saved.type === current.type
              
              console.log('VizSettings comparison:', {
                saved: {
                  colorBy: saved.colorBy,
                  colorHeader: saved.colorHeader,
                  zoneClassification: saved.zoneClassification,
                  zoneStops: saved.zoneStops,
                  type: saved.type
                },
                current: {
                  colorBy: current.colorBy,
                  colorHeader: current.colorHeader,
                  zoneClassification: current.zoneClassification,
                  zoneStops: current.zoneStops,
                  type: current.type
                },
                match: vizSettingsMatch
              })
              
              if (vizSettingsMatch) {
                
                // Store loaded rates in reactive data property using Vue.set for reactivity
                this.loadedZoneRates = {}
                if (savedSettings.zoneRates) {
                  savedSettings.zoneRates.forEach(savedZone => {
                    // Store by both key and value for flexible matching
                    this.$set(this.loadedZoneRates, savedZone.key, savedZone.rate)
                    this.$set(this.loadedZoneRates, savedZone.value, savedZone.rate)
                  })
                }
                
                // Set flag to trigger reactivity
                this.zoneRatesLoaded = true
                console.log('Stored loaded zone rates:', this.loadedZoneRates)
                
                this.$store.commit('setAlert', {
                  active: true,
                  message: 'Loaded previously saved zone rates.',
                  level: 'alert-info',
                  timeout: 3000
                })
              } else {
                console.log('VizSettings changed, not loading saved rates')
                // Clear any previously loaded rates
                this.loadedZoneRates = {}
              }
            }
          } else {
            // No saved rates found, clear loaded rates
            console.log('No saved zone rates found')
            this.loadedZoneRates = {}
          }
        })
        .catch(error => {
          console.error('Error loading zone rates:', error)
          this.loadedZoneRates = {}
        })
    },
    sendRateToQT (rate) {
      console.log('sendRateToQT called with rate:', rate)
      
      if (this.websocketConnected && this.websocket) {
        try {
          // Send rate as JSON for better structure
          const message = JSON.stringify({
            type: 'rate',
            value: rate,
            timestamp: new Date().toISOString()
          })
          
          console.log('Sending rate via WebSocket:', message)
          this.websocket.send(message)
        } catch (error) {
          console.log('Failed to send rate via WebSocket:', error)
        }
      } else {
        console.log('WebSocket not connected, simulating rate send:', rate)
      }
    },
    toggleSimulationMode () {
      if (this.isSimulationMode) {
        this.stopSimulation()
      } else {
        this.startSimulation()
      }
    },
    startSimulation () {
      // Stop any existing location tracking if running
      this.stopLocationTracking()
      
      this.isSimulationMode = true
      this.currentSimulationIndex = 0
      
      // Get the dataset bounds for simulation
      const datasetBounds = this.getDatasetBounds()
      
      if (!datasetBounds) {
        this.$store.commit('setAlert', {
          active: true,
          message: 'No dataset bounds available for simulation.',
          level: 'alert-warning',
          timeout: 3000
        })
        this.isSimulationMode = false
        return
      }
      
      // Create a simulation path within the dataset bounds
      this.simulationPath = this.generateSimulationPath(datasetBounds)
      
      console.log('Starting GPS simulation with', this.simulationPath.length, 'points within dataset bounds')
      
      // Start the simulation interval
      this.simulationInterval = setInterval(() => {
        if (this.currentSimulationIndex < this.simulationPath.length) {
          const point = this.simulationPath[this.currentSimulationIndex]
          
          // Calculate bearing to next point
          if (this.currentSimulationIndex > 0) {
            const prevPoint = this.simulationPath[this.currentSimulationIndex - 1]
            this.userBearing = this.calculateBearing(
              prevPoint.lat, prevPoint.lng,
              point.lat, point.lng
            )
          }
          
          // Update user location
          this.userLocation = {
            lat: point.lat,
            lng: point.lng,
            accuracy: 2.0 // Simulated high accuracy
          }
          
          // Update map
          this.updateUserLocationOnMap()
          
          if (this.fullScreenMap) {
            this.fullScreenMap.easeTo({
              center: [this.userLocation.lng, this.userLocation.lat],
              bearing: this.userBearing,
              duration: 1000
            })
          }
          
          // Check for zone intersection
          this.checkZoneIntersection()
          
          this.currentSimulationIndex++
        } else {
          // Restart the simulation
          this.currentSimulationIndex = 0
        }
      }, 500) // Move every 0.5 seconds for smoother movement
      
      this.$store.commit('setAlert', {
        active: true,
        message: 'GPS simulation started. Tractor will move through the dataset area.',
        level: 'alert-info',
        timeout: 3000
      })
    },
    stopSimulation () {
      this.isSimulationMode = false
      if (this.simulationInterval) {
        clearInterval(this.simulationInterval)
        this.simulationInterval = null
      }
      
      this.$store.commit('setAlert', {
        active: true,
        message: 'GPS simulation stopped.',
        level: 'alert-info',
        timeout: 2000
      })
    },
    getDatasetBounds () {
      const renderedData = this.getRenderedData()
      if (!renderedData || !renderedData.features || renderedData.features.length === 0) {
        return null
      }
      
      try {
        // Calculate bounding box of the dataset
        const bbox = turf.bbox(renderedData)
        return {
          minLng: bbox[0],
          minLat: bbox[1],
          maxLng: bbox[2],
          maxLat: bbox[3]
        }
      } catch (error) {
        console.error('Error calculating dataset bounds:', error)
        return null
      }
    },
    generateSimulationPath (datasetBounds) {
      // const path = []
      const { minLat, maxLat, minLng, maxLng } = datasetBounds
      
      // Calculate grid size based on dataset size
      const latRange = maxLat - minLat
      const lngRange = maxLng - minLng
      const gridSize = Math.min(latRange, lngRange) / 8 // 8x8 grid within dataset
      
      // Add some padding to ensure we cover the full area
      const padding = gridSize * 0.5
      const startLat = minLat - padding
      const endLat = maxLat + padding
      const startLng = minLng - padding
      const endLng = maxLng + padding
      
      const rows = Math.ceil((endLat - startLat) / gridSize)
      const cols = Math.ceil((endLng - startLng) / gridSize)
      
      console.log('Generating simulation path:', {
        bounds: datasetBounds,
        gridSize: gridSize,
        rows: rows,
        cols: cols,
        padding: padding
      })
      
      // Create diagonal paths through the bounding box
      const diagonalPaths = []
      
      // Path 1: Top-left to bottom-right
      const diagonal1 = []
      for (let i = 0; i < Math.max(rows, cols); i++) {
        const lat = startLat + (i / Math.max(rows, cols)) * (endLat - startLat)
        const lng = startLng + (i / Math.max(rows, cols)) * (endLng - startLng)
        diagonal1.push({ lat, lng })
      }
      diagonalPaths.push(...diagonal1)
      
      // Path 2: Top-right to bottom-left
      const diagonal2 = []
      for (let i = 0; i < Math.max(rows, cols); i++) {
        const lat = startLat + (i / Math.max(rows, cols)) * (endLat - startLat)
        const lng = endLng - (i / Math.max(rows, cols)) * (endLng - startLng)
        diagonal2.push({ lat, lng })
      }
      diagonalPaths.push(...diagonal2)
      
      // Path 3: Bottom-left to top-right
      const diagonal3 = []
      for (let i = 0; i < Math.max(rows, cols); i++) {
        const lat = endLat - (i / Math.max(rows, cols)) * (endLat - startLat)
        const lng = startLng + (i / Math.max(rows, cols)) * (endLng - startLng)
        diagonal3.push({ lat, lng })
      }
      diagonalPaths.push(...diagonal3)
      
      // Path 4: Bottom-right to top-left
      const diagonal4 = []
      for (let i = 0; i < Math.max(rows, cols); i++) {
        const lat = endLat - (i / Math.max(rows, cols)) * (endLat - startLat)
        const lng = endLng - (i / Math.max(rows, cols)) * (endLng - startLng)
        diagonal4.push({ lat, lng })
      }
      diagonalPaths.push(...diagonal4)
      
      // Interpolate between points to create smoother movement
      const interpolatedPath = []
      for (let i = 0; i < diagonalPaths.length - 1; i++) {
        const current = diagonalPaths[i]
        const next = diagonalPaths[i + 1]
        
        // Add the current point
        interpolatedPath.push(current)
        
        // Calculate distance between points
        const distance = this.calculateDistance(current.lat, current.lng, next.lat, next.lng)
        const interpolationSteps = Math.max(5, Math.floor(distance / 0.0001)) // More steps for longer distances
        
        // Interpolate intermediate points
        for (let step = 1; step < interpolationSteps; step++) {
          const ratio = step / interpolationSteps
          const interpolatedLat = current.lat + (next.lat - current.lat) * ratio
          const interpolatedLng = current.lng + (next.lng - current.lng) * ratio
          interpolatedPath.push({ lat: interpolatedLat, lng: interpolatedLng })
        }
      }
      
      // Add the last point
      if (diagonalPaths.length > 0) {
        interpolatedPath.push(diagonalPaths[diagonalPaths.length - 1])
      }
      
      console.log('Interpolated diagonal path:', {
        originalPoints: diagonalPaths.length,
        interpolatedPoints: interpolatedPath.length,
        averageDistance: this.calculateAverageDistance(interpolatedPath)
      })
      
      return interpolatedPath
    },
    calculateBearing (lat1, lng1, lat2, lng2) {
      // Calculate bearing between two points
      const toRad = (deg) => deg * Math.PI / 180
      const toDeg = (rad) => rad * 180 / Math.PI
      
      const dLng = toRad(lng2 - lng1)
      const lat1Rad = toRad(lat1)
      const lat2Rad = toRad(lat2)
      
      const y = Math.sin(dLng) * Math.cos(lat2Rad)
      const x = Math.cos(lat1Rad) * Math.sin(lat2Rad) - Math.sin(lat1Rad) * Math.cos(lat2Rad) * Math.cos(dLng)
      
      const bearing = toDeg(Math.atan2(y, x))
      return (bearing + 360) % 360
    },
    calculateDistance (lat1, lng1, lat2, lng2) {
      // Calculate distance between two points in degrees
      const dLat = lat2 - lat1
      const dLng = lng2 - lng1
      return Math.sqrt(dLat * dLat + dLng * dLng)
    },
    calculateAverageDistance (path) {
      if (path.length < 2) return 0
      
      let totalDistance = 0
      for (let i = 0; i < path.length - 1; i++) {
        totalDistance += this.calculateDistance(
          path[i].lat, path[i].lng,
          path[i + 1].lat, path[i + 1].lng
        )
      }
      return totalDistance / (path.length - 1)
    },
    updateUserLocationOnMap () {
      if (!this.fullScreenMap || !this.userLocation) return
      
      const userLocationSource = this.fullScreenMap.getSource('user-location')
      if (userLocationSource) {
        userLocationSource.setData({
          type: 'FeatureCollection',
          features: [
            {
              type: 'Feature',
              geometry: {
                type: 'Point',
                coordinates: [this.userLocation.lng, this.userLocation.lat]
              },
              properties: {
                bearing: this.userBearing,
                accuracy: this.userLocation.accuracy
              }
            }
          ]
        })
      }
    },
    formatZoneValue (zone) {
      // For zone rate configuration, always show zone numbers (1, 2, 3, 4, 5...)
      // Use the zoneNumber property if available
      if (zone.zoneNumber) {
        return `Zone ${zone.zoneNumber}`
      }
      
      // Fallback: use zoneIndex to determine the zone number
      if (zone.zoneIndex !== undefined && zone.zoneIndex !== null) {
        // Convert 0-based index to 1-based zone number
        return `Zone ${zone.zoneIndex + 1}`
      }
      
      // Fallback: try to extract zone number from the key or value
      if (zone.key && /^\d+$/.test(zone.key)) {
        return `Zone ${zone.key}`
      }
      
      if (zone.value && /^\d+$/.test(zone.value.toString())) {
        return `Zone ${zone.value}`
      }
      
      // If we can't determine a zone number, show the original value
      if (zone.category) {
        return zone.category
      }
      
      if (zone.value !== undefined && zone.value !== null) {
        // For categorical data, return the value as-is
        if (this.selectedDatasetData && this.selectedDatasetData.vizSettings && 
            this.selectedDatasetData.vizSettings.zoneClassification === 'categorical') {
          return zone.value.toString()
        }
        
        // For numerical data, format as number
        const numValue = Number(zone.value)
        if (!isNaN(numValue)) {
          return numValue.toFixed(2)
        }
        
        // For non-numerical data, return as string
        return zone.value.toString()
      }
      
      return 'N/A'
    },
    addZoneNumbersToPolygons (polygonData, vizSettings) {
      // Add zone numbers to polygon datasets for consistent zone labeling
      if (!polygonData || !polygonData.features || polygonData.features.length === 0) {
        return polygonData
      }
      
      const colorHeader = vizSettings.colorHeader
      if (!colorHeader) {
        return polygonData
      }
      
      // Create a copy of the data to avoid modifying the original
      const dataWithZones = JSON.parse(JSON.stringify(polygonData))
      
      // Group polygons by their values to create zones
      const zoneGroups = new Map()
      
      dataWithZones.features.forEach((feature, index) => {
        const value = feature.properties[colorHeader]
        if (value !== undefined && value !== null) {
          const zoneKey = value.toString()
          
          if (!zoneGroups.has(zoneKey)) {
            zoneGroups.set(zoneKey, [])
          }
          zoneGroups.get(zoneKey).push(feature)
        }
      })
      
      // Assign zone numbers to each group
      const zoneKeys = Array.from(zoneGroups.keys())
      zoneKeys.forEach((zoneKey, zoneIndex) => {
        const features = zoneGroups.get(zoneKey)
        features.forEach(feature => {
          feature.properties.zone = zoneKey
          feature.properties.zoneNumber = (zoneIndex + 1).toString() // 1-based zone numbers
        })
      })
      
      console.log('Added zone numbers to polygons:', zoneKeys.length, 'zones')
      return dataWithZones
    },
    getRenderedData () {
      // Return the data that's actually being rendered on the map
      // For point datasets, this should be the generated zones
      // For polygon datasets, this is the original data
      
      // Check if we have generated zones (for point datasets)
      if (this.generatedZones && this.generatedZones.features && this.generatedZones.features.length > 0) {
        return this.generatedZones
      }
      
      // Fall back to original dataset
      if (this.selectedDatasetGeoJson) {
        return this.selectedDatasetGeoJson
      }
      
      return null
    },
    getZoneIndexFromValue (value, vizSettings) {
      // Determine the zone index for a given value based on visualization settings
      console.log('Getting zone index for value:', value, 'with settings:', vizSettings)
      
      if (vizSettings.zoneClassification === 'equal-interval') {
        const stopDiff = vizSettings.maxValue - vizSettings.minValue
        if (stopDiff === 0) return 0
        
        const normalizedValue = (value - vizSettings.minValue) / stopDiff
        const zoneIndex = Math.floor(normalizedValue * vizSettings.zoneStops)
        console.log('Equal-interval zone index:', zoneIndex, 'for normalized value:', normalizedValue)
        return Math.max(0, Math.min(zoneIndex, vizSettings.zoneStops - 1))
      } else if (vizSettings.zoneClassification === 'jenks' && vizSettings.breaks) {
        for (let i = 0; i < vizSettings.breaks.length - 1; i++) {
          if (value >= vizSettings.breaks[i] && value <= vizSettings.breaks[i + 1]) {
            console.log('Jenks zone index:', i, 'for value:', value)
            return i
          }
        }
        console.log('Value not found in Jenks breaks, returning 0')
        return 0
      } else if (vizSettings.zoneClassification === 'categorical') {
        // For categorical data, find the category index
        if (vizSettings.featureCategories) {
          for (let i = 0; i < vizSettings.featureCategories.length; i++) {
            if (vizSettings.featureCategories[i].name === value) {
              console.log('Categorical zone index:', i, 'for value:', value)
              return i
            }
          }
        }
        
        // If the value is a zone number (1, 2, 3, etc.), convert it to 0-based index
        console.log('Checking if value is zone number:', value, 'type:', typeof value, 'is string:', typeof value === 'string', 'is digits:', /^\d+$/.test(value))
        if ((typeof value === 'string' && /^\d+$/.test(value)) || (typeof value === 'number' && value >= 1 && value <= 10)) {
          const zoneIndex = parseInt(value) - 1 // Convert to 0-based index
          console.log('Zone number converted to index:', zoneIndex, 'for zone:', value)
          return Math.max(0, zoneIndex)
        }
        
        console.log('Category not found, returning 0')
        return 0
      }
      
      console.log('No classification found, returning 0')
      return 0
    },
    getZoneColorFromIndex (zoneIndex, vizSettings) {
      // Get color directly from zone index
      console.log('Getting color for zoneIndex:', zoneIndex, 'from colors:', vizSettings.colors)
      if (!vizSettings.colors || vizSettings.colors.length === 0) {
        console.log('No colors available, using default')
        return '#cccccc'
      }
      const color = vizSettings.colors[Math.min(zoneIndex, vizSettings.colors.length - 1)] || '#cccccc'
      console.log('Selected color:', color)
      return color
    },
    getZoneColorFromValue (value, vizSettings) {
      // Determine the color for a given value based on visualization settings
      if (!vizSettings.colors || vizSettings.colors.length === 0) {
        return '#cccccc'
      }
      
      if (vizSettings.zoneClassification === 'equal-interval') {
        const stopDiff = vizSettings.maxValue - vizSettings.minValue
        const normalizedValue = (value - vizSettings.minValue) / stopDiff
        const zoneIndex = Math.floor(normalizedValue * vizSettings.zoneStops)
        return vizSettings.colors[Math.min(zoneIndex, vizSettings.colors.length - 1)]
      } else if (vizSettings.zoneClassification === 'jenks' && vizSettings.breaks) {
        for (let i = 0; i < vizSettings.breaks.length - 1; i++) {
          if (value >= vizSettings.breaks[i] && value <= vizSettings.breaks[i + 1]) {
            return vizSettings.colors[Math.min(i, vizSettings.colors.length - 1)]
          }
        }
      }
      
      return vizSettings.colors[0] || '#cccccc'
    },
    getZoneColor (zone, index) {
      if (zone.color) {
        return zone.color
      }
      
      if (!this.selectedDatasetData || !this.selectedDatasetData.vizSettings) {
        return '#cccccc'
      }
      
      const vizSettings = this.selectedDatasetData.vizSettings
      if (vizSettings.colors && vizSettings.colors[index]) {
        return vizSettings.colors[index]
      }
      
      return '#cccccc'
    },
    generateZonePolygons (datasetGeoJson, vizSettings) {
      console.log('Starting zone generation...')
      // Only generate zones for point datasets with value-based coloring
      if (!datasetGeoJson.features.length || 
          datasetGeoJson.features[0].geometry.type !== 'Point' ||
          vizSettings.colorBy !== 'valueBased') {
        console.log('Zone generation conditions not met, returning null')
        return null
      }

      try {
        // Generate bounding box
        // eslint-disable-next-line no-undef
        const bbox = turf.bbox(datasetGeoJson)
        console.log('Bbox generated:', bbox)
        
        // Generate concave hull
        // eslint-disable-next-line no-undef
        const hull = turf.concave(datasetGeoJson, { units: 'kilometers', maxEdge: 0.05 })
        // eslint-disable-next-line no-undef
        const bufferedHull = turf.buffer(hull, 0.003, { units: 'kilometers' })
        
        // Generate Voronoi Polygons in place of points
        // eslint-disable-next-line no-undef
        const voronoiPolygons = turf.voronoi(datasetGeoJson, { bbox: bbox })
        
        // Attach properties to those polygons
        for (let i = 0; i < datasetGeoJson.features.length; i++) {
          voronoiPolygons.features[i].properties = datasetGeoJson.features[i].properties
        }
        
        // Test all coordinate pairs to see if they are within the hull. Splice out if not
        voronoiPolygons.features.forEach(feature => {
          let wasTrimmed = false
          for (let i = feature.geometry.coordinates[0].length - 1; i >= 0; i--) {
            // eslint-disable-next-line no-undef
            const pointToCheck = turf.point(feature.geometry.coordinates[0][i])
            // eslint-disable-next-line no-undef
            const isInside = turf.booleanPointInPolygon(pointToCheck, bufferedHull)
            if (!isInside) {
              feature.geometry.coordinates[0].splice(i, 1)
              wasTrimmed = true
            }
          }
          if (wasTrimmed) {
            feature.geometry.coordinates[0].push(feature.geometry.coordinates[0][0])
          }
        })
        
        // Prepare empty zones
        const zones = {}
        if (vizSettings.categorical) {
          for (let i = 0; i < vizSettings.featureCategories.length; i++) {
            zones[vizSettings.featureCategories[i].name] = []
          }
        } else {
          for (let i = 0; i < vizSettings.zoneStops; i++) {
            zones[i] = []
          }
        }
        
        // Push each polygon feature into the appropriate zone
        voronoiPolygons.features.forEach(polygon => {
          if (polygon.geometry.coordinates[0].length > 3) { // Polygons must have at least 4 coordinate pairs
            if (vizSettings.categorical) {
              zones[polygon.properties[vizSettings.colorHeader]].push(polygon)
            } else if (vizSettings.zoneClassification === 'equal-interval' || vizSettings.zoneClassification === 'jenks') {
              for (let i = 0; i < vizSettings.zoneStops; i++) {
                const zoneMinMax = {}
                if (vizSettings.zoneClassification === 'equal-interval') {
                  const stopDiff = vizSettings.maxValue - vizSettings.minValue
                  const stopLength = 1 / vizSettings.zoneStops
                  const stopMultiplierMin = stopLength * i
                  const stopMultiplierMax = stopLength * (i + 1)
                  zoneMinMax.min = (stopDiff * stopMultiplierMin) + vizSettings.minValue
                  zoneMinMax.max = (stopDiff * stopMultiplierMax) + vizSettings.minValue
                }
                if (vizSettings.zoneClassification === 'jenks') {
                  zoneMinMax.min = vizSettings.breaks[i]
                  zoneMinMax.max = vizSettings.breaks[i + 1]
                }
                if (i === vizSettings.zoneStops - 1) { // if last zone include the max value in the zone
                  if (polygon.properties[vizSettings.colorHeader] >= zoneMinMax.min && polygon.properties[vizSettings.colorHeader] <= zoneMinMax.max) {
                    zones[i].push(polygon)
                  }
                } else {
                  if (polygon.properties[vizSettings.colorHeader] >= zoneMinMax.min && polygon.properties[vizSettings.colorHeader] < zoneMinMax.max) {
                    zones[i].push(polygon)
                  }
                }
              }
            }
          }
        })
        
        // Combine polygons
        const zoneKeys = Object.keys(zones).sort((a, b) => parseInt(a) - parseInt(b)) // Sort zone keys numerically
        const zonesGeoJson = {
          type: 'FeatureCollection',
          features: []
        }
        
        zoneKeys.forEach((key, zoneIndex) => {
          let zoneUnion = false
          let zoneValue = null
          let zoneCount = 0
          
          zones[key].forEach((zoneKeyFeature, i) => {
            if (!zoneUnion) {
              zoneUnion = zoneKeyFeature
              // Get the value from the first feature in this zone
              zoneValue = zoneKeyFeature.properties[vizSettings.colorHeader]
              zoneCount = 1
            } else {
              // eslint-disable-next-line no-undef
              zoneUnion = turf.union(zoneUnion, zoneKeyFeature)
              // Accumulate values for averaging
              zoneValue += zoneKeyFeature.properties[vizSettings.colorHeader]
              zoneCount++
            }
          })
          
          if (zoneUnion) {
            // Calculate average value for the zone
            const averageValue = zoneValue / zoneCount
            
            zoneUnion.properties = {
              zone: key,
              zoneNumber: (zoneIndex + 1).toString(), // Add incremental zone number (1, 2, 3, 4, 5...)
              [vizSettings.colorHeader]: averageValue // Add the color header value
            }
            zonesGeoJson.features.push(zoneUnion)
          }
        })
        
        return zonesGeoJson
      } catch (error) {
        console.error('Error in generateZonePolygons:', error)
        return null
      }
    },
    selectDataset (dataset) {
      this.selectedDataset = dataset.id
      console.log('Selecting dataset:', dataset.id)
      
      // Clear any previously loaded rates
      this.loadedZoneRates = {}
      
      this.loadDatasetData(dataset).then(() => {
        console.log('Dataset data loaded, loading zone rates...')
        // Load saved zone rates after dataset data is loaded
        this.loadZoneRatesFromFirebase().then(() => {
          console.log('Zone rates loading completed')
          // Force a reactive update and wait for next tick
          this.$nextTick(() => {
            this.$forceUpdate()
            console.log('Forced update completed')
          })
        })
      })
    },
    async loadDatasetData (dataset) {
      try {
        console.log('Loading dataset data for:', dataset.id)
        
        // Show loading spinner
        this.mapLoading = true
        
        // Clear any existing map only if we're loading a different dataset
        if (this.previewMap && this.selectedDataset !== dataset.id) {
          console.log('Clearing existing map for new dataset...')
          this.previewMap.remove()
          this.previewMap = null
        }
        
        // Load the full dataset data
        const datasetDoc = await fb.datasetsCollection.doc(dataset.id).get()
        this.selectedDatasetData = datasetDoc.data()
        this.selectedDatasetData.id = datasetDoc.id
        
        console.log('Dataset metadata loaded')
        
        // Load the GeoJSON data
        if (this.selectedDatasetData.file && this.selectedDatasetData.file.geoJsonPath) {
          console.log('Loading GeoJSON data...')
          const geoJsonRef = firebase.storage().ref(this.selectedDatasetData.file.geoJsonPath)
          const geoJsonUrl = await geoJsonRef.getDownloadURL()
          const response = await fetch(geoJsonUrl)
          this.selectedDatasetGeoJson = await response.json()
          
          console.log('GeoJSON data loaded, features:', this.selectedDatasetGeoJson.features.length)
          
          // Wait for next tick to ensure DOM is ready, then initialize map
          this.$nextTick(() => {
            setTimeout(() => {
              this.initializePreviewMap()
            }, 300) // Increased delay for better reliability
          })
        } else {
          console.error('No GeoJSON path found in dataset')
          this.mapLoading = false
        }
      } catch (error) {
        console.error('Error loading dataset data:', error)
        this.mapLoading = false
      }
    },
    isVRCompatible (dataset) {
      // Check if dataset exists and has required properties
      if (!dataset || !dataset.vizSettings || dataset.vizSettings.colorBy !== 'valueBased') {
        return false
      }
      
      // Check if one of the compatible zone classifications is present
      const compatibleClassifications = ['polygon', 'categorical', 'equal-interval', 'jenks']
      return compatibleClassifications.includes(dataset.vizSettings.type) || 
             compatibleClassifications.includes(dataset.vizSettings.zoneClassification)
    },
    getSelectedDataset () {
      return this.availableDatasets.find(d => d.id === this.selectedDataset)
    },
    goToDatasetSettings () {
      // Navigate to the dataset details page to configure settings
      const farmId = this.currentFarm?.id
      if (farmId) {
        this.$router.push({ path: `/f/${farmId}/dataset/${this.selectedDataset}?mypad=1` })
      } else {
        this.$router.push({ path: '/f' })
      }
    },
    initializePreviewMap () {
      console.log('Initializing preview map...')
      
      if (!this.selectedDatasetGeoJson || !this.selectedDatasetData) {
        console.log('Dataset data not ready, skipping map initialization')
        return
      }
      
      // Check if the container element exists
      const container = document.getElementById('variableRatePreviewMap')
      if (!container) {
        console.log('Map container not found. Retrying in 200ms...')
        setTimeout(() => {
          this.initializePreviewMap()
        }, 200)
        return
      }
      
      // Ensure container is visible and has dimensions
      if (container.offsetWidth === 0 || container.offsetHeight === 0) {
        console.log('Map container has no dimensions. Retrying in 200ms...')
        setTimeout(() => {
          this.initializePreviewMap()
        }, 200)
        return
      }
      
      // Additional check: ensure container is actually visible and rendered
      const computedStyle = window.getComputedStyle(container)
      if (computedStyle.display === 'none' || computedStyle.visibility === 'hidden' || computedStyle.opacity === '0') {
        console.log('Container not visible, retrying in 200ms...')
        setTimeout(() => {
          this.initializePreviewMap()
        }, 200)
        return
      }
      
      // Ensure container is in the DOM and has proper styling
      if (!document.body.contains(container)) {
        console.log('Container not in DOM, retrying in 200ms...')
        setTimeout(() => {
          this.initializePreviewMap()
        }, 200)
        return
      }
      
      console.log('Container ready, calculating bounds...')
      
      // Calculate bounds first
      // eslint-disable-next-line no-undef
      const bounds = turf.bbox(this.selectedDatasetGeoJson)
      const center = [
        (bounds[0] + bounds[2]) / 2, // longitude center
        (bounds[1] + bounds[3]) / 2 // latitude center
      ]
      
      // Calculate appropriate zoom level based on bounds
      const boundsWidth = bounds[2] - bounds[0]
      const boundsHeight = bounds[3] - bounds[1]
      const maxDimension = Math.max(boundsWidth, boundsHeight)
      
      // Calculate zoom level - smaller bounds = higher zoom
      let zoom = 15 // Default zoom for small areas
      if (maxDimension > 1) zoom = 10 // Large areas
      else if (maxDimension > 0.1) zoom = 12 // Medium areas
      else if (maxDimension > 0.01) zoom = 14 // Small areas
      
      console.log('Bounds calculated, initializing map...')
      
      // Clean up existing map if it exists
      if (this.previewMap) {
        console.log('Map already exists, cleaning up and reinitializing...')
        this.previewMap.remove()
        this.previewMap = null
      }
      
      try {
        // eslint-disable-next-line no-undef
        this.previewMap = new mapboxgl.Map({
          container: 'variableRatePreviewMap',
          style: 'mapbox://styles/mapbox/satellite-v9',
          center: center,
          zoom: zoom,
          preserveDrawingBuffer: true, // Ensure proper rendering
          failIfMajorPerformanceCaveat: false, // Allow fallback rendering
          antialias: true // Enable antialiasing for better rendering
        })
        
        console.log('Map created, waiting for load event...')
        
        // Wait for map to load, then visualize the dataset and fit bounds
        this.previewMap.on('load', () => {
          console.log('Map loaded, visualizing dataset...')
          
                      // Check if the map canvas is properly rendered
            setTimeout(() => {
              const canvas = this.previewMap.getCanvas()
              if (canvas && canvas.width > 0 && canvas.height > 0) {
                console.log('Map canvas properly rendered:', canvas.width, 'x', canvas.height)
                
                // Force the canvas to be visible
                canvas.style.display = 'block'
                canvas.style.visibility = 'visible'
                canvas.style.opacity = '1'
                
                // Also ensure the map container is visible
                const mapContainer = this.previewMap.getContainer()
                if (mapContainer) {
                  mapContainer.style.display = 'block'
                  mapContainer.style.visibility = 'visible'
                  mapContainer.style.opacity = '1'
                }
              } else {
                console.warn('Map canvas not properly rendered, forcing re-render...')
                // Force the map to re-render
                this.previewMap.resize()
              }
            }, 100)
          
          this.visualizeDataset()
          
                            // Wait for next tick to ensure zones are generated and zoneRates computed
          this.$nextTick(() => {
            console.log('Map initialization nextTick - checking map status...')
            this.checkMapStatus()
            
            // Fit to bounds with padding for better view
            this.previewMap.fitBounds(bounds, { padding: 20 })
            
            // Force map to resize and render properly
            setTimeout(() => {
              console.log('Forcing map resize to ensure proper rendering...')
              this.previewMap.resize()
              
              // Force a complete map re-render
              setTimeout(() => {
                console.log('Forcing map re-render...')
                if (this.previewMap && this.previewMap.loaded()) {
                  // Force the map to re-render by triggering a style change
                  this.previewMap.triggerRepaint()
                  
                  // Also try forcing a resize again
                  this.previewMap.resize()
                  
                  this.checkMapStatus()
                }
              }, 200)
              
              this.checkMapStatus()
            }, 100)
            
            // Hide loading spinner after map is fully loaded
            this.mapLoading = false
            console.log('Map initialization complete')
            
            // Start monitoring the map container for changes
            this.startMapMonitoring()
            
            // Check map status after completion
            setTimeout(() => {
              console.log('Map status after initialization:')
              this.checkMapStatus()
            }, 1000)
          })
        })
        
        // Add error handler
        this.previewMap.on('error', (error) => {
          console.error('Map error:', error)
          this.mapLoading = false
        })
        
      } catch (error) {
        console.error('Error initializing map:', error)
        this.mapLoading = false
      }
    },
    visualizeDataset () {
      if (!this.previewMap || !this.selectedDatasetGeoJson || !this.selectedDatasetData) return
      
      const vizSettings = this.selectedDatasetData.vizSettings || {}
      
      // Remove existing layers if they exist
      if (this.previewMap.getLayer('vrPreviewLayer')) {
        this.previewMap.removeLayer('vrPreviewLayer')
      }
      if (this.previewMap.getSource('vrPreviewSource')) {
        this.previewMap.removeSource('vrPreviewSource')
      }
      
      // Determine what data to visualize
      let visualizationData = this.selectedDatasetGeoJson
      let visualizationType = 'circle'
      
      // For point datasets with value-based coloring, generate zones for better VR visualization
      if (vizSettings.type === 'circle' && 
          vizSettings.colorBy === 'valueBased' && 
          this.selectedDatasetGeoJson.features.length > 0 &&
          this.selectedDatasetGeoJson.features[0].geometry.type === 'Point') {
        
        try {
          // Generate zone polygons from points
          const zonesGeoJson = this.generateZonePolygons(this.selectedDatasetGeoJson, vizSettings)
          if (zonesGeoJson && zonesGeoJson.features.length > 0) {
            visualizationData = zonesGeoJson
            visualizationType = 'polygon'
            // Store the generated zones for zone rate configuration
            this.generatedZones = zonesGeoJson
          }
        } catch (error) {
          console.error('Error generating zones, falling back to points:', error)
          // Fall back to point visualization if zone generation fails
          visualizationType = 'circle'
          this.generatedZones = null
        }
      } else if (vizSettings.type === 'polygon') {
        // For polygon datasets, add zone numbers to the original data
        visualizationType = 'polygon'
        const polygonDataWithZones = this.addZoneNumbersToPolygons(this.selectedDatasetGeoJson, vizSettings)
        visualizationData = polygonDataWithZones
        this.generatedZones = polygonDataWithZones
      }
      
      // Add GeoJSON source
      this.previewMap.addSource('vrPreviewSource', {
        type: 'geojson',
        data: visualizationData
      })
      
      // Get color and size expressions
      const colorExpression = this.getMapboxColor(vizSettings, visualizationData)
      const sizeExpression = this.getMapboxSize(vizSettings)
      
      // Add layer based on visualization type
      if (visualizationType === 'polygon') {
        this.previewMap.addLayer({
          id: 'vrPreviewLayer',
          type: 'fill',
          source: 'vrPreviewSource',
          paint: {
            'fill-color': colorExpression,
            'fill-opacity': vizSettings.opacity || 0.7,
            'fill-outline-color': '#000000'
          }
        })
        
        // Add zone labels for polygon layers
        this.previewMap.addLayer({
          id: 'zone-labels',
          type: 'symbol',
          source: 'vrPreviewSource',
          layout: {
            'text-field': ['get', 'zoneNumber'],
            'text-size': 14,
            'text-anchor': 'center',
            'text-offset': [0, 0],
            'text-allow-overlap': true,
            'text-ignore-placement': true
          },
          paint: {
            'text-color': '#000000',
            'text-halo-color': '#ffffff',
            'text-halo-width': 2
          }
        })
        
        // Add click handler for preview map
        this.previewMap.on('click', 'vrPreviewLayer', (e) => {
          if (e.features.length > 0) {
            const feature = e.features[0]
            const zoneNumber = feature.properties.zoneNumber || 'Unknown'
            const vizSettings = this.selectedDatasetData.vizSettings
            
            // Determine what information to show based on dataset type
            let infoContent = ''
            
            if (vizSettings.zoneClassification === 'categorical') {
              // For categorical data, show the category name
              const actualValue = feature.properties[vizSettings.colorHeader] || 'N/A'
              infoContent = `<strong>Category:</strong> ${actualValue}`
            } else {
              // For numerical data (equal-interval, jenks), show zone range or just zone info
              if (vizSettings.zoneClassification === 'equal-interval' && vizSettings.minValue !== false && vizSettings.maxValue !== false) {
                const zoneIndex = parseInt(zoneNumber) - 1
                const totalZones = vizSettings.zoneStops
                const range = vizSettings.maxValue - vizSettings.minValue
                const zoneSize = range / totalZones
                const zoneMin = vizSettings.minValue + (zoneIndex * zoneSize)
                const zoneMax = zoneIndex === totalZones - 1 ? vizSettings.maxValue : vizSettings.minValue + ((zoneIndex + 1) * zoneSize)
                infoContent = `<strong>Value Range:</strong> ${zoneMin.toFixed(2)} - ${zoneMax.toFixed(2)}`
              } else {
                // For other types, just show zone info
                infoContent = `<strong>Zone Type:</strong> ${vizSettings.zoneClassification}`
              }
            }
            
            // Show popup with zone information
            // eslint-disable-next-line no-undef
            new mapboxgl.Popup()
              .setLngLat(e.lngLat)
              .setHTML(`
                <div style="padding: 10px;">
                  <h6 style="margin: 0 0 8px 0; color: #333;">Zone ${zoneNumber}</h6>
                  <p style="margin: 0; font-size: 14px; color: #666;">
                    ${infoContent}
                </p>
              </div>
            `)
            .addTo(this.previewMap)
          }
        })
        
        // Change cursor to pointer when hovering over zones in preview
        this.previewMap.on('mouseenter', 'vrPreviewLayer', () => {
          this.previewMap.getCanvas().style.cursor = 'pointer'
        })
        
        this.previewMap.on('mouseleave', 'vrPreviewLayer', () => {
          this.previewMap.getCanvas().style.cursor = ''
        })
      } else {
        // Default to circle for points
        this.previewMap.addLayer({
          id: 'vrPreviewLayer',
          type: 'circle',
          source: 'vrPreviewSource',
          paint: {
            'circle-radius': sizeExpression,
            'circle-color': colorExpression,
            'circle-opacity': vizSettings.opacity || 0.7
          }
        })
      }
    },
    getMapboxColor (vizSettings, dataSource = null) {
      if (!vizSettings.colorBy || vizSettings.colorBy === 'solid') {
        return vizSettings.circleColor || '#ffc107'
      }
      
      if (vizSettings.colorBy === 'valueBased' && vizSettings.colorHeader) {
        const colorHeader = vizSettings.colorHeader
        
        // Default colors array
        if (!vizSettings.colors) {
          vizSettings.colors = [
            '#77CCCC', '#777711', '#AA7744', '#AAAA44', '#774411', '#4477AA',
            '#AA4455', '#771155', '#DDDD77', '#117744', '#DDAA77', '#CC99BB',
            '#44AAAA', '#117777', '#77AADD', '#88CCAA', '#DD7788', '#114477',
            '#AA4488', '#771122', '#44AA77'
          ]
        }
        
        // Use provided data source or fall back to selectedDatasetGeoJson
        const dataToAnalyze = dataSource || this.selectedDatasetGeoJson
        
        // Process the data to determine min/max values and categories
        let minValue = false
        let maxValue = false
        let categorical = false
        const featureCategories = []
        
        // Analyze the data
        dataToAnalyze.features.forEach((feature, i) => {
          let featureValue = feature.properties[colorHeader]
          if (featureValue === null || !featureValue) {
            featureValue = 0
            dataToAnalyze.features[i].properties[colorHeader] = 0
          }
          
          const notNumber = isNaN(featureValue)
          if (notNumber) {
            categorical = true
          } else {
            if (typeof featureValue === 'string') {
              featureValue = Number(featureValue)
              dataToAnalyze.features[i].properties[colorHeader] = featureValue
            }
            if (minValue === false) {
              minValue = featureValue
              maxValue = featureValue
            }
            if (featureValue < minValue) minValue = featureValue
            if (featureValue > maxValue) maxValue = featureValue
          }
        })
        
        // Update vizSettings with calculated values
        vizSettings.minValue = minValue
        vizSettings.maxValue = maxValue
        vizSettings.categorical = categorical
        
        // Handle categorical data
        if (categorical) {
          const categoryCounts = {}
          this.selectedDatasetGeoJson.features.forEach(feature => {
            const categoryValue = feature.properties[colorHeader]
            if (typeof categoryCounts[categoryValue] === 'undefined') {
              categoryCounts[categoryValue] = 1
            } else {
              categoryCounts[categoryValue]++
            }
          })
          
          const entries = Object.entries(categoryCounts)
          entries.forEach((entry, i) => {
            if (i < 20) {
              if (entry[0] === '0') {
                featureCategories.push({
                  name: 'No Value',
                  count: entry[1],
                  color: '#333'
                })
              } else {
                featureCategories.push({
                  name: entry[0],
                  count: entry[1],
                  color: vizSettings.colors[i]
                })
              }
            }
          })
          
          // Create categorical color expression
          const color = ['match', ['get', colorHeader]]
          featureCategories.forEach(category => {
            color.push(category.name)
            color.push(category.color)
          })
          color.push('#333')
          return color
        }
        
        // Handle numerical data based on zone classification
        if (vizSettings.zoneClassification === 'linear') {
          return [
            'interpolate',
            ['linear'],
            ['to-number', ['get', colorHeader]],
            minValue,
            vizSettings.minColor || '#ffc107',
            maxValue,
            vizSettings.maxColor || '#056839'
          ]
        }
        
        if (vizSettings.zoneClassification === 'equal-interval') {
          const color = [
            'step',
            ['to-number', ['get', colorHeader]],
            'black',
            minValue, vizSettings.colors[0]
          ]
          const stopDiff = maxValue - minValue
          const stopLength = 1 / vizSettings.zoneStops
          for (let i = 1; i < vizSettings.zoneStops; i++) {
            const stopMultiplier = stopLength * i
            const stopFormula = (stopDiff * stopMultiplier) + minValue
            color.push(stopFormula)
            color.push(vizSettings.colors[i])
          }
          return color
        }
        
        if (vizSettings.zoneClassification === 'jenks') {
          // Calculate Jenks breaks
          const breaks = jenks(dataToAnalyze, colorHeader, vizSettings.zoneStops)
          const color = [
            'step',
            ['to-number', ['get', colorHeader]],
            'black'
          ]
          breaks.forEach((breakItem, i) => {
            if (i < breaks.length - 1) {
              color.push(breaks[i])
              color.push(vizSettings.colors[i])
            }
          })
          return color
        }
        
        if (vizSettings.zoneClassification === 'categorical') {
          // Treat numerical data as categorical
          const uniqueValues = [...new Set(dataToAnalyze.features.map(f => f.properties[colorHeader]))]
          const color = ['match', ['get', colorHeader]]
          uniqueValues.forEach((value, i) => {
            color.push(value)
            color.push(vizSettings.colors[i % vizSettings.colors.length])
          })
          color.push('#333')
          return color
        }
      }
      
      return '#ffc107'
    },
    getMapboxSize (vizSettings) {
      if (vizSettings.sizeBy === 'fixed') {
        return vizSettings.circleRadius || 6
      }
      
      if (vizSettings.sizeBy === 'valueBased' && vizSettings.sizeHeader) {
        const sizeHeader = vizSettings.sizeHeader
        
        // Process the data to determine min/max size values
        let minSizeValue = false
        let maxSizeValue = false
        let sizeCategorical = false
        
        // Analyze the data
        this.selectedDatasetGeoJson.features.forEach((feature, i) => {
          let featureSizeValue = feature.properties[sizeHeader]
          
          const notNumber = isNaN(featureSizeValue)
          if (notNumber) {
            sizeCategorical = true
          } else {
            if (typeof featureSizeValue === 'string') {
              featureSizeValue = Number(featureSizeValue)
              this.selectedDatasetGeoJson.features[i].properties[sizeHeader] = featureSizeValue
            }
            if (minSizeValue === false) {
              minSizeValue = featureSizeValue
              maxSizeValue = featureSizeValue
            }
            if (featureSizeValue < minSizeValue) minSizeValue = featureSizeValue
            if (featureSizeValue > maxSizeValue) maxSizeValue = featureSizeValue
          }
        })
        
        if (sizeCategorical) {
          return vizSettings.circleRadius || 6
        }
        
        // Create size interpolation expression
        return [
          'interpolate',
          ['linear'],
          ['to-number', ['get', sizeHeader]],
          minSizeValue,
          vizSettings.circleRadiusMin || 5,
          maxSizeValue,
          vizSettings.circleRadiusMax || 20
        ]
      }
      
      return 6
    },

    launchFolder (folder, fromCrumb) {
      if (this.breadcrumbs.length < 1) {
        this.breadcrumbs = [{ name: 'Root', id: 'root' }]
      }
      if (fromCrumb) {
        const newBreadcrumbs = []
        this.breadcrumbs.some(crumb => {
          if (crumb.id !== folder.id) {
            newBreadcrumbs.push(crumb)
          } else {
            newBreadcrumbs.push(crumb)
            return true
          }
        })
        this.breadcrumbs = newBreadcrumbs
      } else {
        this.breadcrumbs.push(folder)
      }
      this.$store.commit('setStateProperty', { property: 'folder', value: folder })
      this.$store.dispatch('fetchDatasets', this.$store.state.farm.id).then(() => {
        this.$store.dispatch('fetchFolders', this.$store.state.farm.id)
      })
    }
  },
  filters: {
    formatDate (val) {
      if (!val) { return '-' }
      const date = val.toDate()
      return moment(date).format('MM/DD/YY')
    }
  }
}
</script>

<style scoped>
/* Leave 56px clear at the top for the shared PluginHeader. */
#variableRateMode {
  padding-top: 56px;
  min-height: 100vh;
  background: #f7f9fa;
}
.preview-container {
  border: 1px solid #dee2e6;
  border-radius: 0.25rem;
  padding: 1rem;
  background-color: #f8f9fa;
}

.rate-summary {
  background-color: white;
  padding: 1rem;
  border-radius: 0.25rem;
  border: 1px solid #dee2e6;
}

.map-preview {
  border: 1px solid #dee2e6;
  border-radius: 0.25rem;
  overflow: hidden;
}

.folder {
  background-color: #eee;
}

.list-container {
  position: relative;
}

.selected-dataset {
  background-color: #007bff !important;
  color: white !important;
}

.selected-dataset:hover {
  background-color: #0056b3 !important;
  color: white !important;
}

.badge.clickable:hover {
  box-shadow: 1px 2px 4px rgba(0,0,0,0.5);
}

.vr-instructions {
  background-color: #f8f9fa;
  border: 1px solid #dee2e6;
  border-radius: 0.25rem;
  padding: 1rem;
  margin-top: 1rem;
}

.vr-instructions h6 {
  color: #495057;
  margin-bottom: 0.75rem;
}

.vr-instructions ul li {
  margin-bottom: 0.5rem;
  color: #6c757d;
}

.vr-instructions ul li i.fa-check {
  margin-right: 0.5rem;
}

.dataset-browser {
  max-height: 80vh;
  overflow-y: auto;
  border: 1px solid #dee2e6;
  border-radius: 0.25rem;
  padding: 1rem;
  background-color: #f8f9fa;
}

.dataset-browser::-webkit-scrollbar {
  width: 8px;
}

.dataset-browser::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 4px;
}

.dataset-browser::-webkit-scrollbar-thumb {
  background: #c1c1c1;
  border-radius: 4px;
}

.dataset-browser::-webkit-scrollbar-thumb:hover {
  background: #a8a8a8;
}

#variableRatePreviewMap {
  background-color: #f8f9fa;
}

.map-spinner {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  text-align: center;
  background-color: rgba(255, 255, 255, 0.9);
  padding: 20px;
  border-radius: 8px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
  z-index: 1000;
}

.zone-rates-table {
  border: 1px solid #ddd;
  border-radius: 8px;
  overflow: hidden;
  margin-bottom: 20px;
}

.zone-rate-row {
  display: flex;
  align-items: center;
  padding: 12px 16px;
  border-bottom: 1px solid #eee;
}

.zone-rate-row:last-child {
  border-bottom: none;
}

.zone-rate-row.header {
  background-color: #f8f9fa;
  font-weight: bold;
  color: #495057;
}

.zone-color {
  width: 60px;
  display: flex;
  justify-content: center;
}

.color-dot {
  width: 20px;
  height: 20px;
  border-radius: 50%;
  border: 2px solid #fff;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
}

.zone-info {
  width: 120px;
  font-weight: 500;
}

.zone-value {
  flex: 1;
  color: #666;
}

.zone-rate {
  width: 200px;
}

.rate-input {
  width: 100%;
  padding: 8px 12px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 14px;
}

.rate-input:focus {
  border-color: #007bff;
  outline: none;
  box-shadow: 0 0 0 2px rgba(0,123,255,0.25);
}

/* Full Screen Variable Rate Map */
.fullscreen-map {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  z-index: 9999;
  background: #000;
}

.fullscreen-map-header {
  position: absolute;
  top: 20px;
  left: 20px;
  z-index: 10000;
  display: flex;
  align-items: center;
  gap: 20px;
}

.current-rate-display {
  background: rgba(0, 0, 0, 0.8);
  color: white;
  padding: 10px 15px;
  border-radius: 5px;
  font-size: 18px;
  font-weight: bold;
}

.rate-label {
  margin-right: 10px;
}

.rate-value {
  color: #28a745;
  font-size: 24px;
}

.gps-status {
  background: rgba(0, 0, 0, 0.8);
  color: white;
  padding: 8px 12px;
  border-radius: 5px;
  font-size: 14px;
  margin-left: 10px;
}

.status-label {
  margin-right: 8px;
  font-weight: bold;
}

.status-value {
  color: #17a2b8;
}

.fullscreen-map-container {
  width: 100%;
  height: 100%;
}
</style> 
